import { BodyFit, ProductReview } from '@/stores/review-store'
import { PaginatedReviewResponse } from '@/types/api'
import { ReviewResponse, ReviewSheetParams } from '@/types/collection'

import { reviewFormatter } from '../formatter/__f.review'
import { httpClient } from './http-client'

export interface MultiReviewRequest {
  fullname: string
  email: string
  user_id: string
  line_items: Array<{
    product_id: string
    variant_id: string
    available: boolean
    score: number // map từ rating
    message: string
    service_rating: number | null
    perceive: string | null
    source: 'order' | 'other'
    images: string[] // nên là URL đã upload
    criteria: string[] // tags -> criteria
    body_fit: BodyFit | null
    height?: string
    weight?: string
  }>
  token: string
}

export interface MultiReviewResponse {
  state: boolean
  message?: string
  data?: {
    discount: {
      title: string
      description: string
      value: number
      min_order_amount: number
      starts_at: string
      ends_at: string
    }
  }
}

// Transform từ store data sang payload cho API
export function toMultiReviewPayload(
  reviewToken: string,
  batch: {
    fullname: string
    email: string
    user_id: string
    source: 'order' | 'other'
  },
  productReviews: Record<string, ProductReview>,
  userData?: {
    height?: number
    weight?: number
  }
): MultiReviewRequest {
  // Nếu BE dùng thang 10, bật MULTI_REVIEW_SCORE_SCALE=2
  const SCORE_SCALE = Number(
    process.env.NEXT_PUBLIC_MULTI_REVIEW_SCORE_SCALE ?? '1'
  )

  const items = Object.values(productReviews).map((r: ProductReview) => {
    const imagesAsUrls = Array.isArray(r.images)
      ? (r.images as (string | File)[]).filter(
          (x): x is string => typeof x === 'string' && !!x
        )
      : []

    return {
      product_id: r.product_id,
      variant_id: r.variant_id,
      available: Boolean(r.available),
      score: Math.max(1, Math.round((r.rating || 1) * SCORE_SCALE)),
      message: r.message?.trim() || '',
      service_rating: r.service_rating ?? null,
      perceive: r.perceive ?? null,
      source: batch.source || 'order',
      images: imagesAsUrls, // Nếu cần upload file → upload trước, lấy URL
      criteria: r.criteria || [],
      body_fit: r.body_fit ?? null,
      // Ưu tiên giá trị từ form, fallback về user data nếu không có
      height:
        r.height || (userData?.height ? userData.height.toString() : undefined),
      weight:
        r.weight || (userData?.weight ? userData.weight.toString() : undefined),
    }
  })

  return {
    fullname: batch.fullname?.trim(),
    email: batch.email?.trim(),
    user_id: batch.user_id,
    line_items: items,
    token: reviewToken,
  }
}

export const __cReviewService = {
  /**
   * Tạo đánh giá cho nhiều sản phẩm cùng lúc
   */
  createMultiReview: async (
    payload: MultiReviewRequest
  ): Promise<MultiReviewResponse> => {
    try {
      const response = await httpClient.V1.post<MultiReviewResponse>(
        '/reviews/multi',
        payload
      )
      return response
    } catch (error) {
      console.error('Error creating multi review:', error)
      throw error
    }
  },

  //
  getReviews: async (productId: string, params?: Record<string, string>) => {
    const response = await httpClient.V1.get<
      PaginatedReviewResponse<ReviewResponse>
    >(
      `/reviews/filter?current_product_id=${productId}${
        params ? `&${new URLSearchParams(params).toString()}` : ''
      }`
    )

    const formattedReview = reviewFormatter.formatReviewPaginated(response)

    return formattedReview
  },
  getReviewBySheetId: async <T>(params: ReviewSheetParams): Promise<T> => {
    const { data } = await httpClient.V1.get<{ data: T }>(
      `/tools/get-data-from-sheet`,
      {
        params,
      }
    )
    return data
  },
}
